import { db } from '../firebase/firebaseConfig';
import { isRoadmapUserCookbook } from './roadmapService';
import {
  collection,
  query,
  getDocs,
  getDoc,
  doc,
  addDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  where,
  orderBy,
  limit,
  increment,
  writeBatch,
  startAfter,
} from 'firebase/firestore';
import LevelingSystem from '../components/SRS/LevelingSystem';
import { fetchCookbookSaveQuota } from './subscriptionQuotaService';
import {
  confirmCookbookDeckNotRemovedByQuotaTrigger,
} from '../utils/cookbookDeckQuotaTriggerWatch';

const COLLECTION = 'community_cookbooks';
const BATCH_SIZE = 450;

const CARD_PROGRESS_FIELDS = new Set([
  'level',
  'current_exp',
  'exp_to_level',
  'currentLineIndex',
  'progress',
]);

function nowIso() {
  return new Date().toISOString();
}

function stripCardForCommunity(cardData) {
  const cleaned = { ...cardData };
  CARD_PROGRESS_FIELDS.forEach((field) => delete cleaned[field]);
  return {
    title: cleaned.title || 'Untitled',
    description: cleaned.description || '',
    solution: cleaned.solution || cleaned.code || '',
    language: cleaned.language || '',
    tags: cleaned.tags || [],
    createdAt: nowIso(),
    updatedAt: nowIso(),
  };
}

function mapSortField(sortBy) {
  switch (sortBy) {
    case 'recent':
      return 'createdAt';
    case 'imports':
      return 'importCount';
    case 'popular':
    case 'likes':
    default:
      return 'likeCount';
  }
}

export function normalizeCommunityLanguageKey(lang) {
  if (!lang || typeof lang !== 'string') return 'other';
  const t = lang.trim().toLowerCase();
  if (t === 'cpp' || t === 'c++' || t.includes('cplusplus')) return 'cpp';
  if (t === 'csharp' || t === 'c#' || t.includes('c-sharp')) return 'csharp';
  if (t === 'js' || t.includes('javascript') || t === 'ecmascript') return 'javascript';
  if (t === 'ts' || t.includes('typescript')) return 'typescript';
  if (t.includes('html') && t.includes('css')) return 'html/css';
  if (t === 'bash' || t.includes('shell')) return 'shell';
  if (t === 'sql') return 'sql';
  return t;
}

function normalizeListLanguage(language) {
  if (!language || language === 'all') return '';
  return normalizeCommunityLanguageKey(language);
}

function normalizeListSort(sortBy) {
  if (sortBy === 'popular') return 'likes';
  return sortBy || 'likes';
}

function sortCookbooksClient(items, sortBy) {
  const field = mapSortField(sortBy);
  return [...items].sort((a, b) => {
    if (field === 'createdAt') {
      return new Date(b.createdAt || 0) - new Date(a.createdAt || 0);
    }
    return (b[field] || 0) - (a[field] || 0);
  });
}

/** Index missing or still building after deploy — safe to retry with a simpler query. */
export function isFirestoreIndexError(err) {
  const code = err?.code || '';
  const message = (err?.message || '').toLowerCase();
  return (
    code === 'failed-precondition' &&
    (message.includes('index') || message.includes('requires an index'))
  );
}

async function listCommunityCookbooksFallback({
  language = '',
  sortBy = 'likes',
  pageSize = 25,
} = {}) {
  const q = query(
    collection(db, COLLECTION),
    where('visibility', '==', 'public'),
    limit(100)
  );
  const snapshot = await getDocs(q);
  let items = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
  if (language) {
    const key = normalizeCommunityLanguageKey(language);
    items = items.filter((c) => normalizeCommunityLanguageKey(c.language) === key);
  }
  return sortCookbooksClient(items, sortBy).slice(0, pageSize);
}

export async function listCommunityCookbooks({
  language = 'all',
  sortBy = 'popular',
  pageSize = 25,
  startAfter: startAfterDoc = null,
} = {}) {
  const lang = normalizeListLanguage(language);
  const sort = normalizeListSort(sortBy);
  const sortField = mapSortField(sort);

  let q = query(
    collection(db, COLLECTION),
    where('visibility', '==', 'public'),
    orderBy(sortField, 'desc'),
    limit(pageSize)
  );

  if (lang) {
    q = query(
      collection(db, COLLECTION),
      where('visibility', '==', 'public'),
      where('language', '==', lang),
      orderBy(sortField, 'desc'),
      limit(pageSize)
    );
  }

  if (startAfterDoc) {
    if (lang) {
      q = query(
        collection(db, COLLECTION),
        where('visibility', '==', 'public'),
        where('language', '==', lang),
        orderBy(sortField, 'desc'),
        startAfter(startAfterDoc),
        limit(pageSize)
      );
    } else {
      q = query(
        collection(db, COLLECTION),
        where('visibility', '==', 'public'),
        orderBy(sortField, 'desc'),
        startAfter(startAfterDoc),
        limit(pageSize)
      );
    }
  }

  try {
    const snapshot = await getDocs(q);
    const docs = snapshot.docs;
    return {
      items: docs.map((d) => ({ id: d.id, ...d.data() })),
      indexBuilding: false,
      lastDoc: docs.length > 0 ? docs[docs.length - 1] : null,
      hasMore: docs.length === pageSize,
    };
  } catch (err) {
    if (!isFirestoreIndexError(err)) throw err;
    console.warn('[communityCookbook] indexed query unavailable, using client fallback', err.message);
    const items = await listCommunityCookbooksFallback({ language: lang, sortBy: sort, pageSize });
    return {
      items,
      indexBuilding: true,
      lastDoc: null,
      hasMore: false,
    };
  }
}

export async function getCommunityCookbook(cookbookId) {
  const ref = doc(db, COLLECTION, cookbookId);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const data = snap.data();
  if (data.visibility !== 'public') return null;
  return { id: snap.id, ...data };
}

export async function getCommunityCookbookCards(cookbookId) {
  const cardsRef = collection(db, `${COLLECTION}/${cookbookId}/cards`);
  const snapshot = await getDocs(cardsRef);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Alias used by CommunityPage split-pane detail pane */
export const listCookbookCards = getCommunityCookbookCards;

export async function listUserCookbooksForPublish(userId) {
  const decksRef = collection(db, `users/${userId}/decks`);
  const snapshot = await getDocs(decksRef);
  return snapshot.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter((deck) => deck.type === 'cookbook' || !deck.type)
    .filter((deck) => !isRoadmapUserCookbook(deck));
}

export async function getDeckCardCount(userId, deckId) {
  const cardsRef = collection(db, `users/${userId}/decks/${deckId}/cards`);
  const snapshot = await getDocs(cardsRef);
  return snapshot.docs.filter((d) => d.data().solution).length;
}

export async function publishCookbook(userId, deckId, overrides = {}, authorProfile = {}) {
  const deckRef = doc(db, `users/${userId}/decks/${deckId}`);
  const deckSnap = await getDoc(deckRef);
  if (!deckSnap.exists()) {
    throw new Error('Cookbook not found in your library.');
  }

  const deckData = deckSnap.data();
  if (isRoadmapUserCookbook(deckData)) {
    throw new Error('Roadmap cookbooks cannot be published to the community. Choose one from your library.');
  }

  const cardsRef = collection(db, `users/${userId}/decks/${deckId}/cards`);
  const cardsSnap = await getDocs(cardsRef);
  const cards = cardsSnap.docs
    .map((d) => stripCardForCommunity(d.data()))
    .filter((c) => c.solution);

  if (cards.length === 0) {
    throw new Error('Add at least one card with a solution before publishing.');
  }

  const timestamp = nowIso();
  const cookbookRef = doc(collection(db, COLLECTION));
  const cookbookDoc = {
    name: overrides.name || deckData.name || 'Untitled Cookbook',
    description: overrides.description ?? deckData.description ?? '',
    language: normalizeCommunityLanguageKey(overrides.language || deckData.language || 'other'),
    tags: overrides.tags ?? deckData.tags ?? [],
    authorUid: userId,
    authorDisplayName: authorProfile.displayName || 'Anonymous',
    authorPhotoURL: authorProfile.photoURL || '',
    sourceDeckId: deckId,
    cardCount: cards.length,
    likeCount: 0,
    importCount: 0,
    createdAt: timestamp,
    updatedAt: timestamp,
    visibility: 'public',
  };

  // Parent doc must exist before card writes — subcollection rules use get() on the cookbook.
  await setDoc(cookbookRef, cookbookDoc);

  for (let i = 0; i < cards.length; i += BATCH_SIZE) {
    const chunk = writeBatch(db);
    const slice = cards.slice(i, i + BATCH_SIZE);
    slice.forEach((card) => {
      const cardRef = doc(collection(db, `${COLLECTION}/${cookbookRef.id}/cards`));
      chunk.set(cardRef, card);
    });
    await chunk.commit();
  }

  return { id: cookbookRef.id, ...cookbookDoc };
}

export async function unpublishCookbook(cookbookId, userId) {
  const ref = doc(db, COLLECTION, cookbookId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Cookbook not found.');
  if (snap.data().authorUid !== userId) throw new Error('Only the author can unpublish.');

  await updateDoc(ref, {
    visibility: 'hidden',
    updatedAt: nowIso(),
  });
}

export async function updateCommunityCookbookMetadata(cookbookId, userId, updates) {
  const ref = doc(db, COLLECTION, cookbookId);
  const snap = await getDoc(ref);
  if (!snap.exists()) throw new Error('Cookbook not found.');
  if (snap.data().authorUid !== userId) throw new Error('Only the author can edit.');

  const allowed = {};
  if (updates.name != null) allowed.name = updates.name;
  if (updates.description != null) allowed.description = updates.description;
  if (updates.tags != null) allowed.tags = updates.tags;
  allowed.updatedAt = nowIso();

  await updateDoc(ref, allowed);
}

export async function hasUserLiked(cookbookId, userId) {
  if (!userId) return false;
  const likeRef = doc(db, `${COLLECTION}/${cookbookId}/likes/${userId}`);
  const snap = await getDoc(likeRef);
  return snap.exists();
}

export async function toggleLike(cookbookId, userId, cookbook) {
  const likeRef = doc(db, `${COLLECTION}/${cookbookId}/likes/${userId}`);
  const cookbookRef = doc(db, COLLECTION, cookbookId);
  const likedRef = doc(db, `users/${userId}/liked_cookbooks/${cookbookId}`);
  const alreadyLiked = await hasUserLiked(cookbookId, userId);
  const timestamp = nowIso();

  if (alreadyLiked) {
    const batch = writeBatch(db);
    batch.delete(likeRef);
    batch.update(cookbookRef, { likeCount: increment(-1), updatedAt: timestamp });
    await batch.commit();
    try {
      await deleteDoc(likedRef);
    } catch (err) {
      console.warn('[communityCookbook] liked_cookbooks mirror delete failed', err);
    }
    return { liked: false, likeCount: Math.max(0, (cookbook.likeCount || 0) - 1) };
  }

  const batch = writeBatch(db);
  batch.set(likeRef, { createdAt: timestamp });
  batch.update(cookbookRef, { likeCount: increment(1), updatedAt: timestamp });
  await batch.commit();

  try {
    await setDoc(likedRef, {
      cookbookId,
      name: cookbook.name || '',
      language: cookbook.language || '',
      authorDisplayName: cookbook.authorDisplayName || '',
      likedAt: timestamp,
    });
  } catch (err) {
    console.warn('[communityCookbook] liked_cookbooks mirror write failed', err);
  }

  return { liked: true, likeCount: (cookbook.likeCount || 0) + 1 };
}

export async function listLikedCookbooks(userId, max = 12) {
  if (!userId) return [];
  const likedRef = collection(db, `users/${userId}/liked_cookbooks`);
  const q = query(likedRef, orderBy('likedAt', 'desc'), limit(max));
  const snapshot = await getDocs(q);
  return snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/** Liked mirror docs resolved to live public community cookbooks. */
export async function listLikedCommunityCookbooks(userId, max = 100) {
  if (!userId) return [];
  const items = await listLikedCookbooks(userId, max);
  const cookbooks = await Promise.all(
    items.map(async (item) => {
      const id = item.cookbookId || item.id;
      return getCommunityCookbook(id);
    })
  );
  return cookbooks.filter(Boolean);
}

/** Total likes received on all community cookbooks published by this author (includes hidden). */
export async function getCommunityLikesReceivedByAuthor(authorUid) {
  if (!authorUid) return 0;
  const q = query(
    collection(db, COLLECTION),
    where('authorUid', '==', authorUid)
  );
  const snapshot = await getDocs(q);
  return snapshot.docs.reduce((sum, d) => sum + (d.data().likeCount || 0), 0);
}

export async function importCommunityCookbook(cookbookId, userId) {
  const quota = await fetchCookbookSaveQuota();
  if (quota.quotaCheckUnavailable) {
    return { ok: false, reason: 'quota_unavailable', quota };
  }
  if (!quota.isPremium && quota.allowed === false) {
    return { ok: false, reason: 'quota_exceeded', quota };
  }

  const cookbook = await getCommunityCookbook(cookbookId);
  if (!cookbook) {
    return { ok: false, reason: 'not_found' };
  }

  const cards = await getCommunityCookbookCards(cookbookId);
  if (cards.length === 0) {
    return { ok: false, reason: 'empty' };
  }

  const levelingSystem = new LevelingSystem(userId);
  const levelingFields = levelingSystem.initializeCookbookLeveling();
  const timestamp = nowIso();

  const decksRef = collection(db, `users/${userId}/decks`);
  const deckRef = await addDoc(decksRef, {
    name: cookbook.name,
    description: cookbook.description || '',
    language: cookbook.language,
    tags: cookbook.tags || [],
    type: 'cookbook',
    imported: true,
    importedFrom: cookbookId,
    importedAt: timestamp,
    createdAt: timestamp,
    lastAccessed: timestamp,
    level: levelingFields.level,
  });

  if (!quota.isPremium) {
    const outcome = await confirmCookbookDeckNotRemovedByQuotaTrigger(deckRef);
    if (!outcome.ok && outcome.removedByMonthlyLimit) {
      return { ok: false, reason: 'removed_by_quota_trigger' };
    }
  }

  const userCardsRef = collection(db, `users/${userId}/decks/${deckRef.id}/cards`);
  for (const card of cards) {
    const cardLeveling = levelingSystem.initializeSnippetLeveling();
    await addDoc(userCardsRef, {
      title: card.title || 'Untitled',
      description: card.description || '',
      solution: card.solution || '',
      language: card.language || cookbook.language,
      tags: card.tags || cookbook.tags || [],
      creationSource: 'community_import',
      createdAt: timestamp,
      updatedAt: timestamp,
      level: cardLeveling.level,
      current_exp: cardLeveling.current_exp,
      exp_to_level: cardLeveling.exp_to_level,
    });
  }

  await updateDoc(doc(db, COLLECTION, cookbookId), {
    importCount: increment(1),
    updatedAt: timestamp,
  });

  return { ok: true, deckId: deckRef.id, newDeckId: deckRef.id };
}
